import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import {
  Globe, Shield, Zap, Server, ArrowRight, ChevronDown, Plus,
  Network, Lock, Layers, Terminal,
} from 'lucide-react';
import { useState } from 'react';

const TELEGRAM_URL = "https://t.me/DZ_CT";

function FaqItem({ num, question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-border/30" data-testid={`faq-${num}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-4 py-4 text-start group">
        <span className="text-[10px] text-primary">{num}</span>
        <span className="flex-1 text-xs group-hover:text-primary transition-colors">{question}</span>
        <Plus className={`h-3 w-3 text-muted-foreground transition-transform duration-200 ${open ? 'rotate-45' : ''}`} />
      </button>
      {open && <p className="pb-4 ps-8 text-[11px] text-muted-foreground leading-relaxed">{answer}</p>}
    </div>
  );
}

export default function Landing() {
  const { t } = useLanguage();

  const features = [
    { icon: Globe, tag: 'records', title: t('features.free.title'), desc: t('features.free.desc') },
    { icon: Zap, tag: 'speed', title: t('features.speed.title'), desc: t('features.speed.desc') },
    { icon: Network, tag: 'network', title: t('features.types.title'), desc: t('features.types.desc') },
    { icon: Shield, tag: 'security', title: t('features.security.title'), desc: t('features.security.desc') },
    { icon: Layers, tag: 'dashboard', title: t('features.easy.title'), desc: t('features.easy.desc') },
    { icon: Lock, tag: 'api', title: t('features.api.title'), desc: t('features.api.desc') },
  ];

  const faqs = [
    { q: 'What DNS record types are supported?', a: 'A (IPv4), AAAA (IPv6), CNAME, and NS records. These cover the most common use cases for pointing subdomains to any server.' },
    { q: 'How quickly do changes propagate?', a: 'Records are created instantly on Cloudflare\'s global network. Most changes propagate within 60 seconds.' },
    { q: 'Is there a free plan?', a: 'Yes. Create up to 2 DNS records completely free. No credit card, no time limit.' },
    { q: 'How to get unlimited records?', a: 'Contact us on Telegram for our premium plan with unlimited records and priority support.' },
    { q: 'What is Cloudflare Proxy?', a: 'Hides your real IP, provides DDoS protection, SSL termination, and edge caching.' },
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      <Navbar />

      {/* Hero */}
      <section className="pt-24 sm:pt-32 pb-16 sm:pb-24 px-4 sm:px-6" data-testid="hero-section">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
            {/* Left */}
            <div>
              {/* Prompt */}
              <div className="flex items-center gap-2 mb-6 animate-fade-in-up">
                <span className="text-primary text-xs">$</span>
                <span className="text-xs text-muted-foreground">dns --init --zone dnslab.biz</span>
                <span className="w-[2px] h-3 bg-primary animate-blink" />
              </div>

              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.15] tracking-tight mb-6 animate-fade-in-up delay-100" data-testid="hero-title">
                <span className="text-foreground">Free </span>
                <span className="text-primary glow-green">DNS</span>
                <br />
                <span className="text-foreground">Management</span>
              </h1>

              <p className="text-sm text-muted-foreground leading-relaxed max-w-md mb-6 animate-fade-in-up delay-200" data-testid="hero-subtitle">
                {t('hero.subtitle')}
              </p>

              {/* Inline tags */}
              <div className="flex flex-wrap gap-3 mb-8 animate-fade-in-up delay-300">
                {['A', 'AAAA', 'CNAME', 'NS'].map((type) => (
                  <span key={type} className="px-2 py-0.5 rounded border border-border/50 text-[10px] text-muted-foreground">
                    {type}
                  </span>
                ))}
                <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                  <Zap className="h-3 w-3 text-primary" /> &lt;60s propagation
                </span>
              </div>

              {/* CTA */}
              <div className="flex flex-wrap gap-3 mb-10 animate-fade-in-up delay-400">
                <Link to="/register">
                  <Button size="lg" className="rounded h-11 px-6 text-xs tracking-wider gap-2" data-testid="hero-cta-register">
                    get started <ArrowRight className="h-3 w-3" />
                  </Button>
                </Link>
                <a href="#pricing">
                  <Button variant="outline" size="lg" className="rounded h-11 px-6 text-xs tracking-wider gap-2" data-testid="hero-cta-plans">
                    view plans <ChevronDown className="h-3 w-3" />
                  </Button>
                </a>
              </div>

              {/* Stats row */}
              <div className="grid grid-cols-3 gap-6 animate-fade-in-up delay-500" data-testid="hero-stats">
                {[
                  { k: 'users', v: '204+' },
                  { k: 'records', v: '489+' },
                  { k: 'uptime', v: '99.9%' },
                ].map((s) => (
                  <div key={s.k}>
                    <div className="text-xl sm:text-2xl font-bold text-foreground">{s.v}</div>
                    <span className="text-[10px] text-muted-foreground">{s.k}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right - Terminal */}
            <div className="animate-fade-in-up delay-200" data-testid="hero-terminal">
              <div className="rounded border border-border/50 bg-card overflow-hidden box-glow-green">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-2 border-b border-border/30">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-red-500/60" />
                    <div className="w-2 h-2 rounded-full bg-yellow-500/60" />
                    <div className="w-2 h-2 rounded-full bg-green-500/60" />
                  </div>
                  <span className="text-[9px] text-muted-foreground">~/zone/dnslab.biz</span>
                  <div className="flex items-center gap-1">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse-dot" />
                    <span className="text-[9px] text-primary">live</span>
                  </div>
                </div>

                {/* Terminal content */}
                <div className="p-4 text-[11px] leading-relaxed space-y-3" dir="ltr">
                  <div>
                    <span className="text-primary">$</span>
                    <span className="text-muted-foreground"> dnslab list --zone dnslab.biz</span>
                  </div>
                  <div className="text-muted-foreground/60 text-[10px]">Fetching records...</div>

                  {/* Table */}
                  <div className="border border-border/30 rounded overflow-hidden">
                    <div className="grid grid-cols-4 gap-0 text-[9px] text-muted-foreground border-b border-border/20 bg-muted/20">
                      <div className="px-3 py-1.5">TYPE</div>
                      <div className="px-3 py-1.5">NAME</div>
                      <div className="px-3 py-1.5">VALUE</div>
                      <div className="px-3 py-1.5 text-end">TTL</div>
                    </div>
                    {[
                      { type: 'A', color: 'text-blue-400', name: 'myapp', val: '192.0.2.10', ttl: 'auto' },
                      { type: 'AAAA', color: 'text-purple-400', name: 'myapp', val: '2001:db8::1', ttl: 'auto' },
                      { type: 'CNAME', color: 'text-emerald-400', name: 'www', val: 'myapp.dnslab.biz', ttl: '300' },
                      { type: 'NS', color: 'text-orange-400', name: '@', val: 'ns1.cloudflare.com', ttl: '86400' },
                    ].map((r, i) => (
                      <div key={i} className="grid grid-cols-4 gap-0 text-[10px] border-b border-border/10 last:border-0 hover:bg-muted/10 transition-colors">
                        <div className="px-3 py-2"><span className={r.color}>{r.type}</span></div>
                        <div className="px-3 py-2 text-foreground">{r.name}</div>
                        <div className="px-3 py-2 text-muted-foreground truncate">{r.val}</div>
                        <div className="px-3 py-2 text-end text-muted-foreground">{r.ttl}</div>
                      </div>
                    ))}
                  </div>

                  {/* Output */}
                  <div className="flex items-center justify-between text-[10px]">
                    <span className="text-muted-foreground">4 records found</span>
                    <span className="text-primary">propagated &#10003;</span>
                  </div>

                  <div className="pt-1">
                    <span className="text-primary">$</span>
                    <span className="text-muted-foreground"> _</span>
                    <span className="w-[2px] h-3 bg-primary inline-block animate-blink" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Marquee */}
      <div className="overflow-hidden border-y border-border/30 py-2.5">
        <div className="animate-marquee flex whitespace-nowrap">
          {[...Array(2)].flatMap(() => ['Anycast DNS', 'DNSSEC Ready', 'DDoS Protected', '<60s Propagation', 'Global CDN', 'REST API', 'Cloudflare Proxy', 'Free Forever']).map((item, i) => (
            <span key={i} className="mx-6 text-[10px] tracking-wider text-muted-foreground/60 uppercase">
              {item}
              <span className="mx-4 text-border">&#8226;</span>
            </span>
          ))}
        </div>
      </div>

      {/* Features */}
      <section id="features" className="py-20 sm:py-28 px-4 sm:px-6" data-testid="features-section">
        <div className="max-w-6xl mx-auto">
          <div className="mb-12">
            <span className="text-[10px] text-primary tracking-wider">// features</span>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mt-2">{t('features.title')}</h2>
            <p className="text-xs text-muted-foreground mt-2 max-w-lg">{t('features.subtitle')}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} className="group p-5 rounded border border-border/30 bg-card/30 hover:border-primary/20 hover:bg-card/60 transition-all duration-300 animate-fade-in-up" style={{ animationDelay: `${i * 60}ms` }} data-testid={`feature-card-${i}`}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-8 h-8 rounded border border-border/40 flex items-center justify-center group-hover:border-primary/30 transition-colors">
                      <Icon className="h-3.5 w-3.5 text-primary" />
                    </div>
                    <span className="text-[9px] text-muted-foreground/50 tracking-wider">{f.tag}</span>
                  </div>
                  <h3 className="text-xs font-semibold mb-1.5">{f.title}</h3>
                  <p className="text-[11px] text-muted-foreground leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 sm:py-28 px-4 sm:px-6 border-t border-border/20" data-testid="pricing-section">
        <div className="max-w-6xl mx-auto">
          <div className="mb-12">
            <span className="text-[10px] text-primary tracking-wider">// pricing</span>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mt-2">{t('pricing.title')}</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl">
            {/* Free */}
            <div className="p-6 rounded border border-border/30 bg-card/30 animate-fade-in-up" data-testid="plan-card-free">
              <span className="text-[9px] text-muted-foreground tracking-wider">plan:free</span>
              <div className="text-2xl font-bold mt-2 mb-1">{t('pricing.free.price')}</div>
              <span className="text-[10px] text-muted-foreground">2 records max</span>
              <div className="my-5 h-px bg-border/20" />
              <ul className="space-y-2.5 mb-6">
                {t('pricing.free.features').map((f, j) => (
                  <li key={j} className="flex items-center gap-2 text-[11px] text-muted-foreground">
                    <span className="text-primary text-[10px]">+</span> {f}
                  </li>
                ))}
              </ul>
              <Link to="/register">
                <Button variant="outline" className="w-full rounded h-9 text-[11px] tracking-wider" data-testid="plan-cta-free">
                  get started <ArrowRight className="ms-1.5 h-3 w-3" />
                </Button>
              </Link>
            </div>

            {/* Pro */}
            <div className="p-6 rounded border border-primary/20 bg-primary/[0.02] box-glow-green animate-fade-in-up delay-100" data-testid="plan-card-pro">
              <span className="text-[9px] text-primary tracking-wider">plan:pro // popular</span>
              <div className="text-2xl font-bold mt-2 mb-1">{t('pricing.pro.price')}</div>
              <span className="text-[10px] text-muted-foreground">unlimited records</span>
              <div className="my-5 h-px bg-border/20" />
              <ul className="space-y-2.5 mb-6">
                {t('pricing.pro.features').map((f, j) => (
                  <li key={j} className="flex items-center gap-2 text-[11px] text-muted-foreground">
                    <span className="text-primary text-[10px]">+</span> {f}
                  </li>
                ))}
              </ul>
              <a href={TELEGRAM_URL} target="_blank" rel="noopener noreferrer">
                <Button className="w-full rounded h-9 text-[11px] tracking-wider" data-testid="plan-cta-pro">
                  contact us <ArrowRight className="ms-1.5 h-3 w-3" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 sm:py-28 px-4 sm:px-6 border-t border-border/20" data-testid="faq-section">
        <div className="max-w-2xl mx-auto">
          <div className="mb-12">
            <span className="text-[10px] text-primary tracking-wider">// faq</span>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mt-2">Frequently Asked</h2>
          </div>
          {faqs.map((faq, i) => (
            <FaqItem key={i} num={String(i + 1).padStart(2, '0')} question={faq.q} answer={faq.a} />
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 border-t border-border/20" data-testid="cta-section">
        <div className="max-w-xl mx-auto text-center">
          <div className="text-[10px] text-muted-foreground tracking-wider mb-4">// end</div>
          <h2 className="text-2xl sm:text-4xl font-bold tracking-tight mb-3">
            Ready to <span className="text-primary glow-green">deploy</span>?
          </h2>
          <p className="text-xs text-muted-foreground mb-6">Propagate your first record in under 60 seconds.</p>

          {/* Terminal CTA */}
          <div className="inline-flex items-center gap-3 rounded border border-border/40 bg-card px-5 py-3 mb-6">
            <span className="text-primary text-xs">$</span>
            <span className="text-xs text-muted-foreground">dnslab register --free</span>
            <Link to="/register">
              <Button size="sm" className="rounded h-7 text-[10px] tracking-wider" data-testid="cta-register">
                run <ArrowRight className="ms-1 h-3 w-3" />
              </Button>
            </Link>
          </div>

          <p className="text-[10px] text-muted-foreground/50 tracking-wider">no credit card required</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 border-t border-border/20" data-testid="footer">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-8">
            <div className="col-span-2 sm:col-span-1">
              <div className="flex items-center gap-2 mb-3">
                <Terminal className="h-4 w-4 text-primary" />
                <span className="text-xs font-semibold">dnslab<span className="text-primary">.biz</span></span>
              </div>
              <p className="text-[10px] text-muted-foreground leading-relaxed mb-3">{t('footer.description')}</p>
              <div className="flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse-dot" />
                <span className="text-[9px] text-muted-foreground tracking-wider">all systems operational</span>
              </div>
            </div>
            <div>
              <span className="text-[9px] text-muted-foreground/50 tracking-wider uppercase block mb-3">navigate</span>
              <div className="space-y-1.5">
                <Link to="/" className="block text-[11px] text-muted-foreground hover:text-primary transition-colors">home</Link>
                <a href="#features" className="block text-[11px] text-muted-foreground hover:text-primary transition-colors">features</a>
                <a href="#pricing" className="block text-[11px] text-muted-foreground hover:text-primary transition-colors">pricing</a>
              </div>
            </div>
            <div>
              <span className="text-[9px] text-muted-foreground/50 tracking-wider uppercase block mb-3">account</span>
              <div className="space-y-1.5">
                <Link to="/login" className="block text-[11px] text-muted-foreground hover:text-primary transition-colors">log in</Link>
                <Link to="/register" className="block text-[11px] text-muted-foreground hover:text-primary transition-colors">sign up</Link>
              </div>
            </div>
            <div>
              <span className="text-[9px] text-muted-foreground/50 tracking-wider uppercase block mb-3">connect</span>
              <div className="space-y-1.5">
                <a href={TELEGRAM_URL} target="_blank" rel="noopener noreferrer" className="block text-[11px] text-muted-foreground hover:text-primary transition-colors" data-testid="footer-telegram">telegram</a>
              </div>
            </div>
          </div>
          <div className="mt-8 pt-4 border-t border-border/10 text-[9px] text-muted-foreground/40 tracking-wider">
            &copy; {new Date().getFullYear()} dnslab.biz &mdash; free dns management powered by cloudflare
          </div>
        </div>
      </footer>
    </div>
  );
}
